import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BookmarkSimple, Heart, ChatCircle, Eye, Books, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const tabs = [
  { id: "saved", label: "Saved", icon: BookmarkSimple, endpoint: "/bookmarks" },
  { id: "liked", label: "Liked", icon: Heart, endpoint: "/articles/liked" },
  { id: "commented", label: "Comments", icon: ChatCircle, endpoint: "/articles/commented" },
];

export default function Library() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("saved");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const endpoint = tabs.find(t => t.id === activeTab).endpoint;
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) {
      setErr("Failed to load your reading list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [activeTab]);

  return (
    <div className="pb-20">
      <div className="mb-10">
        <div className="eyebrow text-muted-foreground mb-2 flex items-center gap-2">
          <Books size={14} weight="duotone" /> Reading List
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-black tracking-tight">Library</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          A collection of essays, stories, and thoughts you've engaged with across the platform.
        </p>
      </div>

      <div className="flex items-center gap-1 border-b border-border mb-8 overflow-x-auto no-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all relative whitespace-nowrap ${
              activeTab === tab.id ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <tab.icon size={18} weight={activeTab === tab.id ? "fill" : "regular"} />
            {tab.label}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground" />
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 w-full bg-secondary/50 animate-pulse border border-border" />
          ))}
        </div>
      ) : err ? (
        <div className="p-12 border border-destructive/20 bg-destructive/5 text-destructive text-center">
          {err}
          <Button onClick={load} variant="link" className="text-destructive underline block mx-auto mt-2">Try again</Button>
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border py-20 text-center">
          <div className="bg-secondary h-16 w-16 rounded-full grid place-items-center mx-auto mb-4">
            {React.createElement(tabs.find(t => t.id === activeTab).icon, { size: 24, className: "text-muted-foreground" })}
          </div>
          <div className="font-serif text-2xl font-bold">Nothing here yet</div>
          <p className="mt-2 text-sm text-muted-foreground">
            Explore articles and engage with them to see them in your library.
          </p>
          <Button asChild className="mt-8 rounded-none bg-foreground text-background hover:bg-foreground/90">
            <Link to="/explore">Explore Essays</Link>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {items.map((a) => (
            <div key={a.id} className="grid grid-cols-12 gap-6 border border-border bg-card p-5 group hover:border-foreground/20 transition-all duration-300">
              <div className="col-span-12 sm:col-span-3">
                <Link to={`/${a.author?.username}/${a.slug}`}>
                  {a.cover_image ? (
                    <img src={fileUrl(a.cover_image)} alt="" className="w-full aspect-[4/3] sm:aspect-square object-cover grayscale-[0.5] group-hover:grayscale-0 transition-all duration-500" />
                  ) : (
                    <div className="w-full aspect-[4/3] sm:aspect-square bg-secondary grid place-items-center text-muted-foreground text-[10px] eyebrow uppercase tracking-widest">Inkspot</div>
                  )}
                </Link>
              </div>
              <div className="col-span-12 sm:col-span-9 flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                   <Link to={`/${a.author?.username}`} className="flex items-center gap-2 group/author">
                      {a.author?.profile_picture ? (
                        <img src={fileUrl(a.author.profile_picture)} alt="" className="h-5 w-5 object-cover" />
                      ) : (
                        <div className="h-5 w-5 bg-secondary grid place-items-center text-[10px] font-serif">{(a.author?.name || "A")[0]}</div>
                      )}
                      <span className="text-xs font-medium group-hover/author:underline">{a.author?.name}</span>
                   </Link>
                   <span className="text-muted-foreground text-[10px]">·</span>
                   <span className="text-muted-foreground text-[10px] eyebrow">
                    {new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                   </span>
                </div>
                
                <Link to={`/${a.author?.username}/${a.slug}`} className="flex-1">
                  <h3 className="font-serif text-2xl font-bold tracking-tight group-hover:underline leading-tight">{a.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2 leading-relaxed">{a.excerpt}</p>
                </Link>

                <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between">
                  <div className="flex items-center gap-4 text-[11px] text-muted-foreground eyebrow tracking-wider">
                    <span className="flex items-center gap-1.5"><Heart size={14} weight="fill" className="text-red-500" /> {a.likes_count}</span>
                    <span className="flex items-center gap-1.5"><ChatCircle size={14} weight="fill" className="text-blue-500" /> {a.comments_count}</span>
                    <span className="flex items-center gap-1.5"><Eye size={14} /> {a.views}</span>
                  </div>
                  <Link to={`/${a.author?.username}/${a.slug}`} className="text-xs font-medium flex items-center gap-1 hover:gap-2 transition-all">
                    Read Essay <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
